import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Upload, FileSpreadsheet, ArrowRight, ArrowLeft, CheckCircle2, AlertTriangle,
  Loader2, Rocket, Building2, Users, Layers, Home, Store, Plus, Trash2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const BLUE = '#2563eb';
const money = (n) => '$' + Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const num = (n) => Number(n || 0).toLocaleString();

const PHASES = [
  'Phase 1 · Planning & Permitting', 'Phase 2 · Site Prep & Foundation', 'Phase 3 · Framing',
  'Phase 4 · Exterior & Systems (Rough-in)', 'Phase 5 · Insulation & Drywall',
  'Phase 6 · Interior Finishes', 'Phase 7 · Fixtures & Exterior Finishing', 'Phase 8 · Final Walkthrough & Handover',
];
const PROC_MSGS = ['Reading your budget…', 'Identifying line items…', 'Matching contractors…', 'Organizing costs into your 8 phases…'];

const PROJECT_TYPES = ['Single Family', 'New Construction', 'Townhomes / Rowhomes', 'Multifamily', 'Mixed-Use', 'Commercial', 'Renovation', 'Other'];

const RES_TYPES = [
  ['studio', 'Studio'], ['1bed', '1 Bed'], ['2bed', '2 Bed'], ['3bed', '3 Bed'], ['4bed', '4 Bed'],
  ['convertible', 'Convertible'], ['townhome', 'Townhome'], ['single_family', 'Single family home'], ['other', 'Other'],
];
const COM_TYPES = [['commercial', 'Commercial / Retail'], ['office', 'Office'], ['restaurant', 'Restaurant']];
const TYPE_LABEL = Object.fromEntries([...RES_TYPES, ...COM_TYPES]);

// Structural / site lines are shared; finish work lives inside units. Mirrors the
// server-side default so the Review step pre-fills each line's "Applies to".
const SHARED_KW = ['permit', 'excavat', 'foundation', 'framing', 'frame', 'roof', 'siding', 'landscap', 'site', 'grading', 'demolition', 'demo', 'concrete', 'footing', 'slab', 'sewer', 'water main', 'utilit', 'driveway', 'survey', 'engineer', 'architect', 'impact fee', 'erosion', 'elevator', 'lobby', 'parking', 'common'];
const defaultScope = (name) => (SHARED_KW.some((k) => (name || '').toLowerCase().includes(k)) ? 'shared' : 'each');

let TMP = 0;
const tmpId = () => `t${++TMP}`;

function getUser() {
  try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
}

// Split math — kept identical to the backend so the Review preview matches the
// stored breakdown. estimated_cost is the line's TOTAL; unit values are derived.
function computeUnitCosts(units, rows) {
  const totalUnits = units.reduce((s, u) => s + (+u.quantity || 0), 0);
  const totalUnitSqft = units.reduce((s, u) => s + (+u.quantity || 0) * (+u.sqft_each || 0), 0);
  const sharedTotal = rows.filter((r) => !r.allocation_scope || r.allocation_scope === 'shared').reduce((s, r) => s + (+r.budget || 0), 0);
  const eachTotal = rows.filter((r) => r.allocation_scope === 'each').reduce((s, r) => s + (+r.budget || 0), 0);
  const typeTotals = {};
  rows.forEach((r) => { if (r.allocation_scope && r.allocation_scope.startsWith('unit:')) typeTotals[r.allocation_scope] = (typeTotals[r.allocation_scope] || 0) + (+r.budget || 0); });
  const sharedRate = totalUnitSqft > 0 ? sharedTotal / totalUnitSqft : 0;
  const eachPerUnit = totalUnits > 0 ? eachTotal / totalUnits : 0;
  const projectTotal = rows.reduce((s, r) => s + (+r.budget || 0), 0);
  const unitRows = units.map((u, idx) => {
    const Q = +u.quantity || 0, S = +u.sqft_each || 0;
    const sharedShare = sharedRate * S;
    const typePerUnit = Q > 0 ? (typeTotals[`unit:${idx}`] || 0) / Q : 0;
    const unitSpecific = eachPerUnit + typePerUnit;
    const costEach = sharedShare + unitSpecific;
    return {
      idx, label: u.label || TYPE_LABEL[u.unit_type] || 'Unit', category: u.category, qty: Q, sqft: S,
      pctShared: totalUnitSqft > 0 ? (S * Q / totalUnitSqft * 100) : 0,
      sharedShare, unitSpecific, costEach, costPerSqft: S > 0 ? costEach / S : 0, subtotal: costEach * Q,
    };
  });
  return { totalUnits, totalUnitSqft, projectTotal, unitRows, avgCostPerUnit: totalUnits > 0 ? projectTotal / totalUnits : 0 };
}

function Stat({ big, label, tone }) {
  return <div className="text-center px-2"><div className={`text-2xl sm:text-3xl font-extrabold ${tone || 'text-slate-900'}`}>{big}</div><div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mt-0.5">{label}</div></div>;
}

function Shell({ children, num: step }) {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      <div className="bg-white border-b border-slate-200"><div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg text-white font-bold flex items-center justify-center" style={{ background: BLUE }}>D</div>
        <span className="font-bold text-slate-900">DrawReady</span>
        <div className="ml-auto flex items-center gap-4">
          <button onClick={() => navigate('/dashboard')} data-testid="np-exit-dashboard" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft className="w-4 h-4" /> Dashboard</button>
          {step && <span className="text-xs text-slate-400">{step}</span>}
        </div>
      </div></div>
      <main className="flex-1"><div className="max-w-5xl mx-auto px-4 py-8">{children}</div></main>
    </div>
  );
}

function ErrBar({ err }) {
  if (!err) return null;
  return <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700 flex items-center gap-2" data-testid="import-error"><AlertTriangle className="w-4 h-4 flex-shrink-0" />{err}</div>;
}

export default function NewProjectVision() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [step, setStep] = useState('create'); // create|units|budget|processing|review|created
  const [proc, setProc] = useState(0);
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState('');
  const [creating, setCreating] = useState(false);
  const [project, setProject] = useState({ name: '', address: '', property_type: 'Single Family' });
  const [units, setUnits] = useState([]);
  const [sharedSpace, setSharedSpace] = useState('');

  useEffect(() => {
    if (step !== 'processing') return;
    setProc(0);
    const t = setInterval(() => setProc((p) => Math.min(p + 1, PROC_MSGS.length - 1)), 800);
    return () => clearInterval(t);
  }, [step]);

  const primary = 'inline-flex items-center gap-1.5 text-white font-semibold rounded-full px-6 py-2.5 text-sm disabled:opacity-60';
  const total = rows.reduce((s, r) => s + (+r.budget || 0), 0);
  const contractors = new Set(rows.map((r) => r.contractor).filter(Boolean)).size;

  // ---- Units helpers ----
  const addUnit = (unit_type, category) => setUnits((us) => [...us, { tempId: tmpId(), unit_type, category, label: '', quantity: 1, sqft_each: '' }]);
  const setUnit = (id, k, v) => setUnits((us) => us.map((u) => (u.tempId === id ? { ...u, [k]: v } : u)));
  const rmUnit = (id) => setUnits((us) => us.filter((u) => u.tempId !== id));

  const uTotals = () => {
    const totalUnits = units.reduce((s, u) => s + (+u.quantity || 0), 0);
    const res = units.filter((u) => u.category === 'residential').reduce((s, u) => s + (+u.quantity || 0), 0);
    const com = units.filter((u) => u.category === 'commercial').reduce((s, u) => s + (+u.quantity || 0), 0);
    const unitSqft = units.reduce((s, u) => s + (+u.quantity || 0) * (+u.sqft_each || 0), 0);
    return { totalUnits, res, com, unitSqft, totalSqft: unitSqft + (+sharedSpace || 0) };
  };

  const goToUnits = () => {
    if (!project.name.trim()) { setErr('Add a project name to continue.'); return; }
    setErr('');
    setStep('units');
  };

  const goToBudget = () => {
    for (const u of units) {
      if (u.unit_type === 'other' && !(u.label || '').trim()) { setErr('Give your "Other" unit a name.'); return; }
      if (!(+u.sqft_each > 0)) { setErr('Every unit needs its sq ft per unit.'); return; }
      if (!(+u.quantity > 0)) { setErr('Every unit needs a quantity of at least 1.'); return; }
    }
    setErr('');
    setStep('budget');
  };

  // ---- Budget ----
  const onPickFile = () => { setErr(''); fileRef.current?.click(); };
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setErr(''); setStep('processing');
    try {
      const fd = new FormData(); fd.append('file', f);
      const { data } = await axios.post(`${API}/api/onboarding/parse-budget`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
      setRows((data.rows || []).map((r) => ({ ...r, allocation_scope: units.length ? defaultScope(r.item) : null })));
      setStep('review');
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'We couldn’t read that file. Try an Excel or CSV export.');
      setStep('budget');
    }
  };
  const startBlank = () => { setRows([]); setStep('review'); };

  const setRow = (i, k, v) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));

  const createProject = async () => {
    const u = getUser();
    if (!u?.id) { setErr('Please sign in again to create your project.'); return; }
    if (!project.name.trim()) { setErr('Add a project name first.'); setStep('create'); return; }
    setErr(''); setCreating(true);
    try {
      const { data } = await axios.post(`${API}/api/onboarding/create-from-import`, {
        user_id: u.id,
        name: project.name.trim(),
        address: project.address.trim(),
        property_type: project.property_type,
        shared_space_sqft: +sharedSpace || 0,
        units: units.map((x) => ({ unit_type: x.unit_type, category: x.category, label: (x.label || '').trim(), quantity: +x.quantity || 0, sqft_each: +x.sqft_each || 0 })),
        rows: rows.map((r) => ({ code: r.code || '', item: r.item, budget: +r.budget || 0, contractor: r.contractor || '', type: r.type || 'Labor', phase: Math.min(Math.max(Number(r.phase) || 1, 1), 8), flag: r.flag || '', allocation_scope: r.allocation_scope || null })),
      }, { timeout: 120000 });
      setStep('created');
      setTimeout(() => navigate(`/project/${data.project_id}`), 1600);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Could not create the project. Please try again.');
      setCreating(false);
    }
  };

  // ======================= STEP 1 — PROJECT DETAILS =======================
  if (step === 'create') return (
    <Shell num="Step 1 of 4">
      <h1 className="text-3xl font-extrabold text-slate-900">Let's build your project</h1>
      <p className="text-slate-500 mt-1">Tell us the basics. Next you'll lay out the units, then bring in your budget.</p>
      <div className="bg-white rounded-2xl border border-slate-200 p-6 mt-6 space-y-4 max-w-2xl">
        <div><label className="text-xs font-semibold text-slate-600">Project name</label><input className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm mt-1" value={project.name} onChange={(e) => setProject({ ...project, name: e.target.value })} placeholder="e.g. Maple Ridge Townhomes" data-testid="np-name" /></div>
        <div><label className="text-xs font-semibold text-slate-600">Project address</label><input className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm mt-1" value={project.address} onChange={(e) => setProject({ ...project, address: e.target.value })} placeholder="Street, City" data-testid="np-address" /></div>
        <div><label className="text-xs font-semibold text-slate-600">Project type</label>
          <select className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm mt-1 bg-white" value={project.property_type} onChange={(e) => setProject({ ...project, property_type: e.target.value })} data-testid="np-type">
            {PROJECT_TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <p className="text-[11px] text-slate-400">Total square footage is calculated from the units you add on the next step.</p>
      </div>
      <ErrBar err={err} />
      <div className="mt-6"><button onClick={goToUnits} className={primary} style={{ background: BLUE }} data-testid="np-continue-details">Continue <ArrowRight className="w-4 h-4" /></button></div>
    </Shell>
  );

  // ======================= STEP 2 — UNITS & SPACES =======================
  if (step === 'units') {
    const t = uTotals();
    return (
      <Shell num="Step 2 of 4">
        <h1 className="text-3xl font-extrabold text-slate-900">Units & spaces</h1>
        <p className="text-slate-500 mt-1">Add every unit in the building. We'll use this to split costs and total your square footage.</p>

        <div className="mt-6 bg-white rounded-2xl border border-slate-200 p-5">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><Home className="w-3.5 h-3.5" /> Residential</div>
          <div className="flex flex-wrap gap-2 mt-2">
            {RES_TYPES.map(([k, l]) => (
              <button key={k} onClick={() => addUnit(k, 'residential')} data-testid={`unit-add-${k}`} className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-full border border-slate-200 bg-white text-slate-700 hover:border-blue-400 hover:text-blue-700"><Plus className="w-3 h-3" /> {l}</button>
            ))}
          </div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1.5 mt-4"><Store className="w-3.5 h-3.5" /> Commercial</div>
          <div className="flex flex-wrap gap-2 mt-2">
            {COM_TYPES.map(([k, l]) => (
              <button key={k} onClick={() => addUnit(k, 'commercial')} data-testid={`unit-add-${k}`} className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-full border border-slate-200 bg-white text-slate-700 hover:border-blue-400 hover:text-blue-700"><Plus className="w-3 h-3" /> {l}</button>
            ))}
          </div>
        </div>

        {units.length > 0 && (
          <div className="mt-4 bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200 text-left"><th className="px-3 py-2">Type</th><th className="px-3 py-2">Name</th><th className="px-3 py-2 w-24">Qty</th><th className="px-3 py-2 w-32">Sq ft / unit</th><th className="px-3 py-2 w-8"></th></tr></thead>
              <tbody>
                {units.map((u) => (
                  <tr key={u.tempId} className="border-b border-slate-50" data-testid={`unit-row-${u.tempId}`}>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1.5 font-medium text-slate-800">
                        {u.category === 'commercial' ? <Store className="w-3.5 h-3.5 text-slate-400" /> : <Home className="w-3.5 h-3.5 text-slate-400" />}
                        {TYPE_LABEL[u.unit_type]}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <input value={u.label} onChange={(e) => setUnit(u.tempId, 'label', e.target.value)} placeholder={u.unit_type === 'other' ? 'Required' : 'Optional'} className={`w-full rounded border px-2 py-1 text-sm ${u.unit_type === 'other' && !u.label ? 'border-amber-300 bg-amber-50/40' : 'border-slate-200'}`} data-testid={`unit-name-${u.tempId}`} />
                    </td>
                    <td className="px-3 py-2"><input type="number" min="1" value={u.quantity} onChange={(e) => setUnit(u.tempId, 'quantity', e.target.value)} className="w-full rounded border border-slate-200 px-2 py-1 text-sm" data-testid={`unit-qty-${u.tempId}`} /></td>
                    <td className="px-3 py-2"><input type="number" min="0" value={u.sqft_each} onChange={(e) => setUnit(u.tempId, 'sqft_each', e.target.value)} placeholder="e.g. 1200" className={`w-full rounded border px-2 py-1 text-sm ${!(+u.sqft_each > 0) ? 'border-amber-300 bg-amber-50/40' : 'border-slate-200'}`} data-testid={`unit-sqft-${u.tempId}`} /></td>
                    <td className="px-3 py-2"><button onClick={() => rmUnit(u.tempId)} className="text-slate-300 hover:text-red-500" data-testid={`unit-remove-${u.tempId}`}><Trash2 className="w-4 h-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 bg-white rounded-xl border border-slate-200 p-4 max-w-md">
          <label className="text-xs font-semibold text-slate-600">Shared space sq ft <span className="font-normal text-slate-400">(optional)</span></label>
          <input type="number" min="0" value={sharedSpace} onChange={(e) => setSharedSpace(e.target.value)} placeholder="Hallways, lobby, parking…" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm mt-1" data-testid="shared-space-sqft" />
          <p className="text-[11px] text-slate-400 mt-1">Counts toward total building sq ft, but is not used when splitting costs.</p>
        </div>

        <div className="mt-5 bg-white rounded-2xl border border-slate-200 p-5 grid grid-cols-2 sm:grid-cols-4 divide-x divide-slate-100" data-testid="units-totals">
          <Stat big={num(t.totalUnits)} label="Total Units" />
          <Stat big={num(t.res)} label="Residential" />
          <Stat big={num(t.com)} label="Commercial" />
          <Stat big={num(t.totalSqft)} label="Total Sq Ft" tone="text-blue-600" />
        </div>

        <ErrBar err={err} />
        <div className="mt-6 flex items-center justify-between">
          <button onClick={() => setStep('create')} className="text-sm text-slate-500 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Back</button>
          <button onClick={goToBudget} className={primary} style={{ background: BLUE }} data-testid="units-continue">Continue <ArrowRight className="w-4 h-4" /></button>
        </div>
      </Shell>
    );
  }

  // ======================= STEP 3 — BUDGET =======================
  if (step === 'budget') return (
    <Shell num="Step 3 of 4">
      <h1 className="text-3xl font-extrabold text-slate-900">Bring in your project budget</h1>
      <p className="text-slate-500 mt-1">Upload the budget or bid sheet you already have — Excel, CSV or PDF. DrawReady reads it and organizes every line into your 8 phases.</p>
      <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv,.pdf,.txt" className="hidden" onChange={onFile} data-testid="budget-file-input" />
      <button onClick={onPickFile} data-testid="upload-budget" className="w-full text-left mt-6 rounded-2xl border-2 border-blue-300 bg-blue-50/40 p-6 hover:shadow-md transition-all">
        <div className="flex items-start gap-4"><div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0"><Upload className="w-6 h-6 text-white" /></div>
          <div className="flex-1"><div className="flex items-center gap-2"><span className="font-bold text-slate-900 text-lg">Upload Budget or Bid Sheet</span><span className="text-[10px] font-bold text-blue-700 bg-blue-100 rounded-full px-2 py-0.5">RECOMMENDED</span></div>
            <p className="text-sm text-slate-600 mt-1">DrawReady identifies your line items, amounts and contractors and organizes them into your 8 project phases.</p>
            <div className="mt-3 inline-flex items-center gap-1.5 text-white text-sm font-semibold rounded-full px-4 py-2" style={{ background: BLUE }}><FileSpreadsheet className="w-4 h-4" /> Choose File <span className="text-blue-200 text-xs">· Excel · CSV · PDF</span></div>
          </div></div>
      </button>
      <div className="mt-3"><button onClick={startBlank} className="text-left rounded-xl border border-slate-200 bg-white p-4 hover:bg-slate-50 w-full" data-testid="start-blank"><div className="font-semibold text-slate-700 text-sm">Start from scratch instead</div><p className="text-xs text-slate-400 mt-0.5">Create the project with the 8 phases and add budget line items yourself later.</p></button></div>
      <ErrBar err={err} />
      <div className="mt-6"><button onClick={() => setStep('units')} className="text-sm text-slate-500 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Back</button></div>
    </Shell>
  );

  if (step === 'processing') return (
    <Shell num="Step 3 of 4">
      <div className="max-w-lg mx-auto text-center py-16">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto" />
        <h2 className="text-2xl font-bold text-slate-900 mt-6">{PROC_MSGS[proc]}</h2>
        <div className="mt-6 space-y-2 text-left max-w-xs mx-auto">
          {PROC_MSGS.map((m, i) => (<div key={m} className={`flex items-center gap-2 text-sm ${i < proc ? 'text-emerald-600' : i === proc ? 'text-slate-800 font-medium' : 'text-slate-300'}`}>{i < proc ? <CheckCircle2 className="w-4 h-4" /> : <div className="w-4 h-4 rounded-full border-2 border-current" />}{m}</div>))}
        </div>
        <p className="text-xs text-slate-400 mt-6">Reading your sheet with AI — this can take a few seconds.</p>
      </div>
    </Shell>
  );

  // ======================= STEP 4 — REVIEW =======================
  if (step === 'review') {
    const uc = computeUnitCosts(units, rows);
    const scopeOptions = [['shared', 'Shared, split by sq ft'], ['each', 'Every unit'], ...units.map((u, idx) => [`unit:${idx}`, `${u.label || TYPE_LABEL[u.unit_type]} only`])];
    const byPhase = {};
    for (let p = 1; p <= 8; p++) byPhase[p] = [];
    rows.forEach((r, idx) => { const p = Math.min(Math.max(+r.phase || 1, 1), 8); byPhase[p].push({ r, idx }); });

    return (
      <Shell num="Step 4 of 4">
        <div className="text-center"><CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" /><h1 className="text-3xl font-extrabold text-slate-900 mt-3">{rows.length ? 'Review your budget' : 'Your project is set up'}</h1><p className="text-slate-500 mt-1">{rows.length ? 'Set how each line splits across your units, then create your project.' : 'Add budget line items after you create the project to see costs and draws.'}</p></div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5 mt-6 grid grid-cols-2 sm:grid-cols-4 divide-x divide-slate-100">
          <Stat big={money(total)} label="Total Budget" />
          <Stat big={num(rows.length)} label="Line Items" />
          <Stat big={num(uc.totalUnits)} label="Units" />
          <Stat big={rows.length ? money(uc.avgCostPerUnit) : '—'} label="Avg / Unit" tone="text-blue-600" />
        </div>

        {rows.length === 0 ? (
          <div className="mt-6 rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-500">No line items yet — you can add budget items after your project is created.</div>
        ) : (
          <>
            {/* Line items grouped under the 8 phases with an Applies-to setting */}
            <div className="mt-6 space-y-4">
              {Object.keys(byPhase).filter((p) => byPhase[p].length).map((p) => (
                <div key={p} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="px-4 py-2 bg-slate-50 border-b border-slate-100 text-xs font-semibold text-slate-600">{PHASES[p - 1]}</div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100 text-left"><th className="px-3 py-2">Line Item</th><th className="px-3 py-2 text-right">Budget</th><th className="px-3 py-2">Applies to</th></tr></thead>
                      <tbody>
                        {byPhase[p].map(({ r, idx }) => (
                          <tr key={idx} className="border-b border-slate-50" data-testid={`review-row-${idx}`}>
                            <td className="px-3 py-2 font-medium text-slate-800">{r.item}{r.contractor ? <span className="block text-[11px] font-normal text-slate-400">{r.contractor}</span> : null}</td>
                            <td className="px-3 py-2 text-right tabular-nums text-slate-700">{money(r.budget)}</td>
                            <td className="px-3 py-2">
                              {units.length ? (
                                <select value={r.allocation_scope || 'shared'} onChange={(e) => setRow(idx, 'allocation_scope', e.target.value)} className="border border-slate-200 rounded px-2 py-1 text-xs bg-white max-w-[220px]" data-testid={`review-applies-${idx}`}>
                                  {scopeOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                </select>
                              ) : <span className="text-xs text-slate-400">Whole project</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>

            {/* Live cost-per-unit table */}
            {units.length > 0 && (
              <div className="mt-6 bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="review-unit-costs">
                <div className="px-4 py-2 bg-slate-50 border-b border-slate-100 text-xs font-semibold text-slate-600">Cost per unit</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200 text-left"><th className="px-3 py-2">Unit</th><th className="px-3 py-2 text-right">Qty</th><th className="px-3 py-2 text-right">Sq Ft</th><th className="px-3 py-2 text-right">% Shared</th><th className="px-3 py-2 text-right">Shared Share</th><th className="px-3 py-2 text-right">Unit-Specific</th><th className="px-3 py-2 text-right">Cost Each</th><th className="px-3 py-2 text-right">$/Sq Ft</th><th className="px-3 py-2 text-right">Subtotal</th></tr></thead>
                    <tbody>
                      {uc.unitRows.map((r) => (
                        <tr key={r.idx} className="border-b border-slate-50">
                          <td className="px-3 py-2 font-medium text-slate-800">{r.label}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-slate-600">{r.qty}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-slate-600">{num(r.sqft)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-slate-500">{r.pctShared.toFixed(1)}%</td>
                          <td className="px-3 py-2 text-right tabular-nums text-slate-600">{money(r.sharedShare)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-slate-600">{money(r.unitSpecific)}</td>
                          <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-900">{money(r.costEach)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-slate-500">${r.costPerSqft.toFixed(2)}</td>
                          <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-900">{money(r.subtotal)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot><tr className="border-t border-slate-200 bg-slate-50"><td className="px-3 py-2 font-semibold text-slate-700" colSpan={8}>Total</td><td className="px-3 py-2 text-right tabular-nums font-bold text-slate-900">{money(uc.unitRows.reduce((s, r) => s + r.subtotal, 0))}</td></tr></tfoot>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        <ErrBar err={err} />
        <div className="mt-6 flex items-center justify-between">
          <button onClick={() => setStep('budget')} className="text-sm text-slate-500 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Back</button>
          <button onClick={createProject} disabled={creating} className={primary} style={{ background: BLUE }} data-testid="create-project">{creating ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating…</> : <>Create My Project <ArrowRight className="w-4 h-4" /></>}</button>
        </div>
      </Shell>
    );
  }

  // ======================= CREATED =======================
  return (
    <Shell>
      <div className="text-center py-6"><div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto"><Rocket className="w-8 h-8 text-emerald-600" /></div><h1 className="text-3xl font-extrabold text-slate-900 mt-4">Your project is ready.</h1><p className="text-slate-500 mt-1">Taking you to your project dashboard…</p></div>
      <div className="bg-white rounded-2xl border border-slate-200 p-6 grid grid-cols-2 sm:grid-cols-4 divide-x divide-slate-100 max-w-2xl mx-auto">
        <Stat big={money(total)} label="Project Budget" /><Stat big={num(rows.length)} label="Budget Items" /><Stat big={num(uTotals().totalUnits)} label="Units" /><Stat big="8" label="DrawReady Phases" tone="text-blue-600" />
      </div>
      <div className="max-w-2xl mx-auto mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[['Add Loan Details', Building2], ['Invite Contractors', Users], ['Upload Documents', FileSpreadsheet], ['Add First Cost', Layers]].map(([t, I]) => (
          <div key={t} className="rounded-xl border border-slate-200 bg-white p-3 text-xs font-medium text-slate-600 flex flex-col items-center gap-1.5"><I className="w-4 h-4 text-slate-400" />{t}</div>
        ))}
      </div>
      <div className="text-center mt-6"><Loader2 className="w-6 h-6 text-blue-600 animate-spin mx-auto" /></div>
    </Shell>
  );
}
